import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box, Button, Tabs, Tab, Typography, Grid, Chip, IconButton, Tooltip, TextField,
    Switch, FormControlLabel, List, ListItem, ListItemText, ListItemSecondaryAction,
    Paper, CircularProgress, Autocomplete, Stack, Avatar, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
    ArrowBack as ArrowBackIcon, Edit as EditIcon, Delete as DeleteIcon,
    Add as AddIcon, Language as LanguageIcon, Schedule as ScheduleIcon,
    Person as PersonIcon, Groups as GroupsIcon, Business as BusinessIcon,
    Code as CodeIcon, Layers as LayersIcon,
    Close as CloseIcon, Save as SaveIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';

import ContentContainer from '@libComponents/ContentContainer';
import { theme } from 'app/Theme';

import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';
import StatusChip from './StatusChip';
import ClientDialog from './ClientDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

const formatPersonName = (entry) => {
    if (!entry) return '';
    const first = entry.profile?.firstname || entry.firstname || '';
    const middle = entry.profile?.middlename || entry.middlename || '';
    const last = entry.profile?.lastname || entry.lastname || '';
    return [first, middle, last].filter(Boolean).join(' ');
};

const formatEmployeeOption = (entry) => {
    if (!entry) return '';
    const name = `${entry.firstname || ''} ${entry.middlename || ''} ${entry.lastname || ''}`.trim();
    const email = entry.user?.email || entry.employeeId || '';
    return name || email;
};

const ScrollableList = ({ children }) => (
    <Box
        sx={{
            maxHeight: { xs: 'unset', md: 480 },
            overflowY: { xs: 'visible', md: 'auto' },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
        }}
    >
        {children}
    </Box>
);

const TabPanel = ({ children, value, index }) =>
    value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;

const ClientDetailsPage = () => {
    const { id: clientIdParam } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const detail = useSelector(selectors.detailSelector);
    const detailLoading = useSelector(selectors.detailLoadingSelector);
    const teamOptions = useSelector(selectors.teamOptionsSelector);
    const teamOptionsLoading = useSelector(selectors.teamOptionsLoadingSelector);
    const employeeOptions = useSelector(selectors.employeeOptionsSelector) || [];
    const employeeOptionsLoading = useSelector(selectors.employeeOptionsLoadingSelector);

    const [tab, setTab] = useState(0);
    const [siteForm, setSiteForm] = useState({ url: '', brand: '', description: '', isPrimary: false });
    const [editingSiteId, setEditingSiteId] = useState(null);
    const [periodForm, setPeriodForm] = useState({ startDate: '', endDate: '', status: '', note: '' });
    const [editingPeriodId, setEditingPeriodId] = useState(null);
    const [repoForm, setRepoForm] = useState({ name: '', url: '' });
    const [editingRepoId, setEditingRepoId] = useState(null);
    const [stackForm, setStackForm] = useState({ name: '' });
    const [editingStackId, setEditingStackId] = useState(null);
    const [empForm, setEmpForm] = useState({ employeeEmail: '', role: '', isActive: true });
    const [teamToAssign, setTeamToAssign] = useState('');
    const [siteDialogOpen, setSiteDialogOpen] = useState(false);
    const [periodDialogOpen, setPeriodDialogOpen] = useState(false);
    const [repoDialogOpen, setRepoDialogOpen] = useState(false);
    const [stackDialogOpen, setStackDialogOpen] = useState(false);
    const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
    const [editEmployeeDialogOpen, setEditEmployeeDialogOpen] = useState(false);
    const [editEmployeeData, setEditEmployeeData] = useState({ employeeEmail: '', role: '', isActive: true });
    const [teamDialogOpen, setTeamDialogOpen] = useState(false);

    const client = detail;
    const clientId = client?.id || clientIdParam;

    const getEmployeeLabelByEmail = (email) => {
        if (!email) return '';
        const match = (employeeOptions || []).find(
            (e) => (e.user?.email || e.employeeId || '').toLowerCase() === String(email).toLowerCase()
        );

        let result = {
            name: formatEmployeeOption(match),
            email: match ? (match.user?.email || match.employeeId || email) : email
        };

        
        return result;
    };

    useEffect(() => {
        if (clientIdParam) {
            dispatch(actions.fetchClientDetail(clientIdParam));
            dispatch(actions.fetchEmployeeOptions());
            dispatch(actions.fetchTeamOptions());
        }
        return () => {
            dispatch(actions.clearClientDetail());
        };
    }, [dispatch, clientIdParam]);

    const handleBack = () => navigate('/clients');
    const handleEdit = () => client && dispatch(actions.openClientDialog(client));
    const handleDelete = () => client && dispatch(actions.openDeleteDialog(client));

    const submitSite = () => {
        if (!siteForm.url.trim()) return;
        const data = {
            url: siteForm.url.trim(),
            brand: siteForm.brand.trim() || undefined,
            description: siteForm.description.trim() || undefined,
            isPrimary: siteForm.isPrimary
        };
        Object.keys(data).forEach((k) => data[k] === undefined && delete data[k]);
        if (editingSiteId) dispatch(actions.updateClientWebsite(clientId, editingSiteId, data));
        else dispatch(actions.addClientWebsite(clientId, data));
        setSiteForm({ url: '', brand: '', description: '', isPrimary: false });
        setEditingSiteId(null);
        setSiteDialogOpen(false);
    };
    const editSite = (s) => {
        setEditingSiteId(s.id);
        setSiteForm({ url: s.url || '', brand: s.brand || '', description: s.description || '', isPrimary: !!s.isPrimary });
        setSiteDialogOpen(true);
    };

    const submitPeriod = () => {
        if (!periodForm.startDate) return;
        const data = {
            startDate: periodForm.startDate,
            endDate: periodForm.endDate || null,
            status: periodForm.status.trim() || undefined,
            note: periodForm.note.trim() || undefined
        };
        Object.keys(data).forEach((k) => data[k] === undefined && delete data[k]);
        if (editingPeriodId) dispatch(actions.updateClientPeriod(clientId, editingPeriodId, data));
        else dispatch(actions.addClientPeriod(clientId, data));
        setPeriodForm({ startDate: '', endDate: '', status: '', note: '' });
        setEditingPeriodId(null);
        setPeriodDialogOpen(false);
    };
    const editPeriod = (p) => {
        setEditingPeriodId(p.id);
        setPeriodForm({
            startDate: p.startDate ? dayjs(p.startDate).format('YYYY-MM-DD') : '',
            endDate: p.endDate ? dayjs(p.endDate).format('YYYY-MM-DD') : '',
            status: p.status || '',
            note: p.note || ''
        });
        setPeriodDialogOpen(true);
    };

    const submitRepo = () => {
        if (!repoForm.name.trim() || !repoForm.url.trim()) return;
        const data = {
            name: repoForm.name.trim(),
            url: repoForm.url.trim()
        };
        if (editingRepoId) dispatch(actions.updateClientGitRepo(clientId, editingRepoId, data));
        else dispatch(actions.addClientGitRepo(clientId, data));
        setRepoForm({ name: '', url: '' });
        setEditingRepoId(null);
        setRepoDialogOpen(false);
    };
    const editRepo = (r) => {
        setEditingRepoId(r.id);
        setRepoForm({ name: r.name || '', url: r.url || '' });
        setRepoDialogOpen(true);
    };

    const submitStack = () => {
        if (!stackForm.name.trim()) return;
        const data = { name: stackForm.name.trim() };
        if (editingStackId) dispatch(actions.updateClientStack(clientId, editingStackId, data));
        else dispatch(actions.addClientStack(clientId, data));
        setStackForm({ name: '' });
        setEditingStackId(null);
        setStackDialogOpen(false);
    };
    const editStack = (s) => {
        setEditingStackId(s.id);
        setStackForm({ name: s.name || '' });
        setStackDialogOpen(true);
    };

    const submitEmployee = () => {
        if (!empForm.employeeEmail.trim()) return;
        dispatch(actions.assignEmployee(clientId, {
            employeeEmail: empForm.employeeEmail.trim(),
            role: empForm.role.trim() || undefined,
            isActive: empForm.isActive
        }));
        setEmpForm({ employeeEmail: '', role: '', isActive: true });
        setEmployeeDialogOpen(false);
    };

    const submitAssignTeam = () => {
        if (!teamToAssign) return;
        dispatch(actions.assignTeam(clientId, teamToAssign));
        setTeamToAssign('');
        setTeamDialogOpen(false);
    };

    const headerControls = (
        <Stack direction='row' spacing={1.5} flexWrap='wrap' alignItems='center' justifyContent='flex-end'>
            <Button variant='outlined' size='small' startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ height: '36px' }}>
                Back to Clients
            </Button>
            <Button variant='outlined' size='small' startIcon={<EditIcon />} onClick={handleEdit} disabled={!client} sx={{ height: '36px' }}>
                Edit
            </Button>
            <Button variant='outlined' color='error' size='small' startIcon={<DeleteIcon />} onClick={handleDelete} disabled={!client} sx={{ height: '36px' }}>
                Delete
            </Button>
        </Stack>
    );

    if (detailLoading && !client) {
        return null;
    }

    if (!detailLoading && !client) {
        return (
            <ContentContainer
                title='Client Not Found'
                headerBg={theme.palette.background.mainHeader}
                AdornmentComponent={
                    <Button variant='outlined' size='small' startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ height: '36px' }}>
                        Back to Clients
                    </Button>
                }
            >
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant='body1' color='text.secondary'>
                        The requested client could not be found.
                    </Typography>
                </Box>
            </ContentContainer>
        );
    }

    const renderOverview = () => (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>Name</Typography>
                <Typography>{client?.name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>Type</Typography>
                <Box sx={{ mt: 0.5 }}>
                    <Chip
                        label={client?.type || '-'}
                        size='small'
                        variant='outlined'
                        color={client?.type === 'PERMANENT' ? 'primary' : 'default'}
                    />
                </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>Industry</Typography>
                <Typography>{client?.industry || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>Country</Typography>
                <Typography>{client?.country || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>Contact Email</Typography>
                <Typography>{client?.contactEmail || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>Contact Phone</Typography>
                <Typography>{client?.contactPhone || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>Dev Manager</Typography>
                <Typography>{getEmployeeLabelByEmail(client?.devManagerEmail).name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>Project Manager</Typography>
                <Typography>{getEmployeeLabelByEmail(client?.projectManagerEmail).name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>Min Hours</Typography>
                <Typography>{client?.minHours !== undefined && client?.minHours !== null ? `${client.minHours} hours` : '-'}</Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant='caption' color='text.secondary'>Description</Typography>
                <Typography>{client?.description || '-'}</Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant='caption' color='text.secondary'>Notes</Typography>
                <Typography>{client?.notes || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>Status</Typography>
                <Box sx={{ mt: 0.5 }}><StatusChip active={!!client?.isActive} /></Box>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>Updated By</Typography>
                <Typography>{client?.updatedBy || client?.createdBy || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>Created At</Typography>
                <Typography>
                    {client?.createdAt ? dayjs(client.createdAt).format('MMM DD, YYYY HH:mm') : '-'}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>Updated At</Typography>
                <Typography>
                    {client?.updatedAt ? dayjs(client.updatedAt).format('MMM DD, YYYY HH:mm') : '-'}
                </Typography>
            </Grid>
        </Grid>
    );

    const renderWebsites = () => (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant='contained'
                    size='small'
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingSiteId(null);
                        setSiteForm({ url: '', brand: '', description: '', isPrimary: false });
                        setSiteDialogOpen(true);
                    }}
                >
                    Add Website
                </Button>
            </Box>
            <Dialog open={siteDialogOpen} onClose={() => setSiteDialogOpen(false)} maxWidth='sm' fullWidth>
                <DialogTitle>{editingSiteId ? 'Edit Website' : 'Add Website'}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ pt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField label='URL' fullWidth size='small' required
                                value={siteForm.url}
                                onChange={(e) => setSiteForm({ ...siteForm, url: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label='Brand' fullWidth size='small'
                                value={siteForm.brand}
                                onChange={(e) => setSiteForm({ ...siteForm, brand: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label='Description' fullWidth size='small'
                                value={siteForm.description}
                                onChange={(e) => setSiteForm({ ...siteForm, description: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={siteForm.isPrimary} onChange={(e) => setSiteForm({ ...siteForm, isPrimary: e.target.checked })} />}
                                label='Primary website'
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSiteDialogOpen(false)}>Cancel</Button>
                    <Button variant='contained' onClick={submitSite}>{editingSiteId ? 'Update' : 'Add'}</Button>
                </DialogActions>
            </Dialog>
            <ScrollableList>
                <List dense disablePadding>
                    {(client?.websites || []).map((s) => (
                        <ListItem key={s.id} divider>
                            <LanguageIcon sx={{ mr: 2, color: 'text.secondary' }} />
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        {s.url}
                                        {s.isPrimary && <Chip label='Primary' size='small' color='primary' />}
                                    </Box>
                                }
                                secondary={[s.brand, s.description].filter(Boolean).join(' • ') || '-'}
                            />
                            <ListItemSecondaryAction>
                                <Tooltip title='Edit'>
                                    <IconButton size='small' onClick={() => editSite(s)}>
                                        <EditIcon fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title='Remove'>
                                    <IconButton size='small' onClick={() => dispatch(actions.deleteClientWebsite(clientId, s.id))}>
                                        <DeleteIcon fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                    {(!client?.websites || client.websites.length === 0) && (
                        <Typography color='text.secondary' sx={{ p: 2, textAlign: 'center' }}>
                            No websites
                        </Typography>
                    )}
                </List>
            </ScrollableList>
        </Box>
    );

    const renderPeriods = () => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant='contained'
                        size='small'
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setEditingPeriodId(null);
                            setPeriodForm({ startDate: '', endDate: '', status: '', note: '' });
                            setPeriodDialogOpen(true);
                        }}
                    >
                        Add Work Period
                    </Button>
                </Box>
                <Dialog open={periodDialogOpen} onClose={() => setPeriodDialogOpen(false)} maxWidth='sm' fullWidth>
                    <DialogTitle>{editingPeriodId ? 'Edit Work Period' : 'Add Work Period'}</DialogTitle>
                    <DialogContent dividers>
                        <Grid container spacing={2} sx={{ pt: 0.5 }}>
                            <Grid item xs={12} sm={6}>
                                <DatePicker
                                    label='Start Date'
                                    value={periodForm.startDate ? dayjs(periodForm.startDate) : null}
                                    onChange={(newValue) => setPeriodForm({ ...periodForm, startDate: newValue ? newValue.format('YYYY-MM-DD') : '' })}
                                    renderInput={(params) => <TextField {...params} fullWidth size='small' required />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DatePicker
                                    label='End Date'
                                    value={periodForm.endDate ? dayjs(periodForm.endDate) : null}
                                    onChange={(newValue) => setPeriodForm({ ...periodForm, endDate: newValue ? newValue.format('YYYY-MM-DD') : '' })}
                                    renderInput={(params) => <TextField {...params} fullWidth size='small' />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label='Status' fullWidth size='small'
                                    value={periodForm.status}
                                    placeholder='e.g. active, paused'
                                    onChange={(e) => setPeriodForm({ ...periodForm, status: e.target.value })} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label='Note' fullWidth size='small'
                                    value={periodForm.note}
                                    onChange={(e) => setPeriodForm({ ...periodForm, note: e.target.value })} />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setPeriodDialogOpen(false)}>Cancel</Button>
                        <Button variant='contained' onClick={submitPeriod}>{editingPeriodId ? 'Update' : 'Add'}</Button>
                    </DialogActions>
                </Dialog>
                <ScrollableList>
                    <List dense disablePadding>
                        {(client?.workPeriods || []).map((p) => (
                            <ListItem key={p.id} divider>
                                <ScheduleIcon sx={{ mr: 2, color: 'text.secondary' }} />
                                <ListItemText
                                    primary={`${dayjs(p.startDate).format('MMM DD, YYYY')} → ${p.endDate ? dayjs(p.endDate).format('MMM DD, YYYY') : 'Ongoing'}`}
                                    secondary={[p.status && `Status: ${p.status}`, p.note].filter(Boolean).join(' • ') || '-'}
                                />
                                <ListItemSecondaryAction>
                                    <Tooltip title='Edit'>
                                        <IconButton size='small' onClick={() => editPeriod(p)}>
                                            <EditIcon fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title='Remove'>
                                        <IconButton size='small' onClick={() => dispatch(actions.deleteClientPeriod(clientId, p.id))}>
                                            <DeleteIcon fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                        {(!client?.workPeriods || client.workPeriods.length === 0) && (
                            <Typography color='text.secondary' sx={{ p: 2, textAlign: 'center' }}>
                                No work periods
                            </Typography>
                        )}
                    </List>
                </ScrollableList>
            </Box>
        </LocalizationProvider>
    );

    const renderGitRepos = () => (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant='contained'
                    size='small'
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingRepoId(null);
                        setRepoForm({ name: '', url: '' });
                        setRepoDialogOpen(true);
                    }}
                >
                    Add Git Repo
                </Button>
            </Box>
            <Dialog open={repoDialogOpen} onClose={() => setRepoDialogOpen(false)} maxWidth='sm' fullWidth>
                <DialogTitle>{editingRepoId ? 'Edit Git Repo' : 'Add Git Repo'}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ pt: 0.5 }}>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                label='Repo Name'
                                fullWidth
                                size='small'
                                required
                                value={repoForm.name}
                                onChange={(e) => setRepoForm({ ...repoForm, name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={7}>
                            <TextField
                                label='Repo URL'
                                fullWidth
                                size='small'
                                required
                                value={repoForm.url}
                                onChange={(e) => setRepoForm({ ...repoForm, url: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRepoDialogOpen(false)}>Cancel</Button>
                    <Button variant='contained' onClick={submitRepo}>{editingRepoId ? 'Update' : 'Add'}</Button>
                </DialogActions>
            </Dialog>
            <ScrollableList>
                <List dense disablePadding>
                    {(client?.gitRepos || []).map((r) => (
                        <ListItem key={r.id} divider>
                            <CodeIcon sx={{ mr: 2, color: 'text.secondary' }} />
                            <ListItemText
                                primary={r.name || r.url}
                                secondary={r.url}
                            />
                            <ListItemSecondaryAction>
                                <Tooltip title='Edit'>
                                    <IconButton size='small' onClick={() => editRepo(r)}>
                                        <EditIcon fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title='Remove'>
                                    <IconButton size='small' onClick={() => dispatch(actions.deleteClientGitRepo(clientId, r.id))}>
                                        <DeleteIcon fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                    {(!client?.gitRepos || client.gitRepos.length === 0) && (
                        <Typography color='text.secondary' sx={{ p: 2, textAlign: 'center' }}>
                            No git repos
                        </Typography>
                    )}
                </List>
            </ScrollableList>
        </Box>
    );

    const renderStacks = () => (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant='contained'
                    size='small'
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingStackId(null);
                        setStackForm({ name: '' });
                        setStackDialogOpen(true);
                    }}
                >
                    Add Stack Item
                </Button>
            </Box>
            <Dialog open={stackDialogOpen} onClose={() => setStackDialogOpen(false)} maxWidth='sm' fullWidth>
                <DialogTitle>{editingStackId ? 'Edit Stack Item' : 'Add Stack Item'}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ pt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField
                                label='Stack Name'
                                fullWidth
                                size='small'
                                required
                                value={stackForm.name}
                                onChange={(e) => setStackForm({ ...stackForm, name: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStackDialogOpen(false)}>Cancel</Button>
                    <Button variant='contained' onClick={submitStack}>{editingStackId ? 'Update' : 'Add'}</Button>
                </DialogActions>
            </Dialog>
            <ScrollableList>
                <List dense disablePadding>
                    {(client?.stacks || []).map((s) => (
                        <ListItem key={s.id} divider>
                            <LayersIcon sx={{ mr: 2, color: 'text.secondary' }} />
                            <ListItemText primary={s.name} />
                            <ListItemSecondaryAction>
                                <Tooltip title='Edit'>
                                    <IconButton size='small' onClick={() => editStack(s)}>
                                        <EditIcon fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title='Remove'>
                                    <IconButton size='small' onClick={() => dispatch(actions.deleteClientStack(clientId, s.id))}>
                                        <DeleteIcon fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                    {(!client?.stacks || client.stacks.length === 0) && (
                        <Typography color='text.secondary' sx={{ p: 2, textAlign: 'center' }}>
                            No stack items
                        </Typography>
                    )}
                </List>
            </ScrollableList>
        </Box>
    );

    const renderEmployees = () => (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant='contained'
                    size='small'
                    startIcon={<AddIcon />}
                    onClick={() => setEmployeeDialogOpen(true)}
                >
                    Assign Employee
                </Button>
            </Box>
            <Dialog open={employeeDialogOpen} onClose={() => setEmployeeDialogOpen(false)} maxWidth='sm' fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box component='span'>Assign Employee</Box>
                    <IconButton size='small' onClick={() => setEmployeeDialogOpen(false)}>
                        <CloseIcon fontSize='small' />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Autocomplete
                            size='small'
                            fullWidth
                            options={Array.isArray(employeeOptions) ? employeeOptions : []}
                            getOptionLabel={(option) => {
                                if (!option) return '';
                                const name = [option.firstname, option.middlename, option.lastname].filter(Boolean).join(' ').trim();
                                const email = option.user?.email || option.employeeId || '';
                                return name ? `${name} (${email})` : email;
                            }}
                            isOptionEqualToValue={(opt, val) =>
                                (opt.user?.email || opt.employeeId) === (val.user?.email || val.employeeId)
                            }
                            value={employeeOptions.find((e) => (e.user?.email || e.employeeId) === empForm.employeeEmail) || null}
                            onChange={(_, newValue) => {
                                const email = newValue ? (newValue.user?.email || newValue.employeeId || '') : '';
                                setEmpForm({ ...empForm, employeeEmail: email });
                            }}
                            loading={employeeOptionsLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Employee'
                                    placeholder='Search by name or email'
                                    required
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {employeeOptionsLoading ? <CircularProgress color='inherit' size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                        />
                        <TextField
                            label='Role'
                            size='small'
                            fullWidth
                            value={empForm.role}
                            onChange={(e) => setEmpForm({ ...empForm, role: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={() => setEmployeeDialogOpen(false)} startIcon={<CloseIcon />}>
                        Cancel
                    </Button>
                    <Button onClick={submitEmployee} variant='contained' startIcon={<SaveIcon />}>
                        Assign
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={editEmployeeDialogOpen} onClose={() => setEditEmployeeDialogOpen(false)} maxWidth='sm' fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box component='span'>Edit Employee Assignment</Box>
                    <IconButton size='small' onClick={() => setEditEmployeeDialogOpen(false)}>
                        <CloseIcon fontSize='small' />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Autocomplete
                            size='small'
                            fullWidth
                            options={Array.isArray(employeeOptions) ? employeeOptions : []}
                            getOptionLabel={(option) => {
                                if (!option) return '';
                                const name = [option.firstname, option.middlename, option.lastname].filter(Boolean).join(' ').trim();
                                const email = option.user?.email || option.employeeId || '';
                                return name ? `${name} (${email})` : email;
                            }}
                            isOptionEqualToValue={(opt, val) =>
                                (opt.user?.email || opt.employeeId) === (val.user?.email || val.employeeId)
                            }
                            value={employeeOptions.find(
                                (e) => (e.user?.email || e.employeeId) === editEmployeeData.employeeEmail
                            ) || null}
                            disabled
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Employee'
                                    placeholder='Search by name or email'
                                    required
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {employeeOptionsLoading ? <CircularProgress color='inherit' size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                        />
                        <TextField
                            label='Role'
                            size='small'
                            fullWidth
                            value={editEmployeeData.role}
                            onChange={(e) => setEditEmployeeData({ ...editEmployeeData, role: e.target.value })}
                        />
                        <FormControlLabel
                            control={<Switch checked={editEmployeeData.isActive} onChange={(e) => setEditEmployeeData({ ...editEmployeeData, isActive: e.target.checked })} />}
                            label={editEmployeeData.isActive ? 'Active' : 'Inactive'}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={() => setEditEmployeeDialogOpen(false)} startIcon={<CloseIcon />}>
                        Cancel
                    </Button>
                    <Button variant='contained' startIcon={<SaveIcon />} onClick={() => {
                        dispatch(actions.updateEmployeeAssignment(clientId, editEmployeeData.employeeEmail, {
                            role: editEmployeeData.role.trim() || undefined,
                            isActive: editEmployeeData.isActive
                        }));
                        setEditEmployeeDialogOpen(false);
                    }}>
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
            <ScrollableList>
                <List dense disablePadding>
                    {(client?.employees || []).map((e) => {
                        return (
                            <ListItem key={e.id || e.employeeEmail} divider>
                                <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <Typography component='span' sx={{ fontWeight: 600 }}>
                                                {getEmployeeLabelByEmail(e.employeeEmail).name || e.employeeEmail}
                                            </Typography>
                                            <StatusChip active={!!e.isActive} />
                                        </Box>
                                    }
                                    secondary={
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', color: 'text.secondary' }}>
                                            {(getEmployeeLabelByEmail(e.employeeEmail).name || formatPersonName(e)) && <span>{e.employeeEmail}</span>}
                                            {e.role && <span>• Role: {e.role}</span>}
                                        </Box>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <Tooltip title='Edit'>
                                        <IconButton size='small' onClick={() => {
                                            setEditEmployeeData({
                                                employeeEmail: e.employeeEmail,
                                                role: e.role || '',
                                                isActive: e.isActive !== undefined ? e.isActive : true
                                            });
                                            setEditEmployeeDialogOpen(true);
                                        }}>
                                            <EditIcon fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title='Unassign'>
                                        <IconButton size='small' onClick={() => dispatch(actions.unassignEmployee(clientId, e.employeeEmail))}>
                                            <DeleteIcon fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                </ListItemSecondaryAction>
                            </ListItem>
                        );
                    })}
                    {(!client?.employees || client.employees.length === 0) && (
                        <Typography color='text.secondary' sx={{ p: 2, textAlign: 'center' }}>
                            No employees assigned
                        </Typography>
                    )}
                </List>
            </ScrollableList>
        </Box>
    );

    const renderTeams = () => (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant='contained'
                    size='small'
                    startIcon={<AddIcon />}
                    onClick={() => setTeamDialogOpen(true)}
                >
                    Assign Team
                </Button>
            </Box>
            <Dialog open={teamDialogOpen} onClose={() => setTeamDialogOpen(false)} maxWidth='sm' fullWidth>
                <DialogTitle>Assign Team</DialogTitle>
                <DialogContent dividers>
                    <Autocomplete
                        size='small'
                        sx={{ mt: 0.5 }}
                        options={(() => {
                            const assignedIds = new Set(
                                (client?.teams || []).map((t) => t.teamId || t.team?.id || t.id)
                            );
                            return (Array.isArray(teamOptions) ? teamOptions : []).filter(
                                (opt) => !assignedIds.has(opt.id)
                            );
                        })()}
                        getOptionLabel={(option) => option?.name || ''}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        value={(teamOptions || []).find((t) => t.id === teamToAssign) || null}
                        onChange={(_, newValue) => setTeamToAssign(newValue ? newValue.id : '')}
                        loading={teamOptionsLoading}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label='Team'
                                placeholder='Search team by name'
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {teamOptionsLoading ? <CircularProgress color='inherit' size={18} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    )
                                }}
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTeamDialogOpen(false)}>Cancel</Button>
                    <Button variant='contained' onClick={submitAssignTeam} disabled={!teamToAssign}>Assign</Button>
                </DialogActions>
            </Dialog>
            <ScrollableList>
                <List dense disablePadding>
                    {(client?.teams || []).map((t, idx, arr) => {
                        const teamId = t.teamId || t.team?.id || t.id;
                        return (
                        <ListItem key={teamId} divider={idx < arr.length - 1} alignItems='flex-start' button onClick={() => navigate(`/teams/${teamId}`)}>
                            <GroupsIcon sx={{ mr: 2, color: 'text.secondary', mt: 1 }} />
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <Typography component='span' sx={{ fontWeight: 600 }}>
                                            {t.team?.name || t.name || t.teamId}
                                        </Typography>
                                        {t.isActive !== false && <StatusChip active />}
                                    </Box>
                                }
                                secondary={
                                    <>
                                        {t.team?.description && (
                                            <Typography variant='body2' color='text.secondary'>
                                                {t.team.description}
                                            </Typography>
                                        )}
                                        {Array.isArray(t.team?.members) && t.team.members.length > 0 && (
                                            <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {t.team.members.map((m) => {
                                                    const name = formatPersonName(m);
                                                    const label = name ? `${name} (${m.employeeEmail})` : m.employeeEmail;
                                                    return (
                                                        <Tooltip key={m.id || m.employeeEmail} title={m.role ? `Role: ${m.role}` : ''}>
                                                            <Chip size='small' variant='outlined' label={label} />
                                                        </Tooltip>
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    </>
                                }
                            />
                            <ListItemSecondaryAction>
                                <Tooltip title='Unassign'>
                                    <IconButton size='small' onClick={() => dispatch(actions.unassignTeam(clientId, t.teamId || t.team?.id))}>
                                        <DeleteIcon fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            </ListItemSecondaryAction>
                        </ListItem>
                    );
                    })}
                    {(!client?.teams || client.teams.length === 0) && (
                        <Typography color='text.secondary' sx={{ p: 2, textAlign: 'center' }}>
                            No teams assigned
                        </Typography>
                    )}
                </List>
            </ScrollableList>
        </Box>
    );

    return (
        <>
            <ContentContainer
                title={client?.name || 'Client Details'}
                headerBg={theme.palette.background.mainHeader}
                AdornmentComponent={headerControls}
            >
                <Box sx={{ width: '100%', p: { xs: 2, sm: 3 } }}>
                    {/* Summary card */}
                    <Paper
                        variant='outlined'
                        sx={{
                            p: { xs: 2, sm: 3 },
                            mb: 3,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: 'wrap'
                        }}
                    >
                        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                            <BusinessIcon />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant='h6' sx={{ fontWeight: 700 }}>
                                {client?.name || '-'}
                            </Typography>
                            {[client?.type, client?.industry, client?.country].filter(Boolean).length > 0 && (
                                <Typography variant='body2' color='text.secondary'>
                                    {[client?.type, client?.industry, client?.country].filter(Boolean).join(' · ')}
                                </Typography>
                            )}
                            {client?.contactEmail && (
                                <Typography
                                    variant='body2'
                                    component='a'
                                    href={`mailto:${client.contactEmail}`}
                                    sx={{ display: 'block', color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    {client.contactEmail}
                                </Typography>
                            )}
                            {client?.contactPhone && (
                                <Typography
                                    variant='body2'
                                    component='a'
                                    href={`tel:${client.contactPhone}`}
                                    sx={{ display: 'block', color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    {client.contactPhone}
                                </Typography>
                            )}
                            {client?.description && (
                                <Typography variant='body2' color='text.secondary'>
                                    {client.description}
                                </Typography>
                            )}
                        </Box>
                        <Stack direction='row' spacing={3} alignItems='center'>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant='caption' color='text.secondary'>Members</Typography>
                                <Typography variant='h6' sx={{ fontWeight: 700 }}>
                                    {client?.memberCount ?? client?.employees?.length ?? 0}
                                </Typography>
                            </Box>
                            <Box>
                                <StatusChip active={!!client?.isActive} />
                            </Box>
                        </Stack>
                    </Paper>

                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        variant='scrollable'
                        allowScrollButtonsMobile
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab label='Overview' />
                        <Tab label={`Websites${client?.websites ? ` (${client.websites.length})` : ''}`} />
                        <Tab label={`Work Periods${client?.workPeriods ? ` (${client.workPeriods.length})` : ''}`} />
                        <Tab label={`Git Repos${client?.gitRepos ? ` (${client.gitRepos.length})` : ''}`} />
                        <Tab label={`Stack${client?.stacks ? ` (${client.stacks.length})` : ''}`} />
                        <Tab label={`Employees${client?.employees ? ` (${client.employees.length})` : ''}`} />
                        <Tab label={`Teams${client?.teams ? ` (${client.teams.length})` : ''}`} />
                    </Tabs>

                    {detailLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <TabPanel value={tab} index={0}>{renderOverview()}</TabPanel>
                            <TabPanel value={tab} index={1}>{renderWebsites()}</TabPanel>
                            <TabPanel value={tab} index={2}>{renderPeriods()}</TabPanel>
                            <TabPanel value={tab} index={3}>{renderGitRepos()}</TabPanel>
                            <TabPanel value={tab} index={4}>{renderStacks()}</TabPanel>
                            <TabPanel value={tab} index={5}>{renderEmployees()}</TabPanel>
                            <TabPanel value={tab} index={6}>{renderTeams()}</TabPanel>
                        </>
                    )}
                </Box>
            </ContentContainer>

            {/* Mounted to support Edit / Delete from this page */}
            <ClientDialog />
            <DeleteConfirmationDialog />
        </>
    );
};

export default ClientDetailsPage;
